'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Expert {
  id: string;
  name: string;
  title: string;
  specialization: string;
  image: string;
  alt: string;
  experience: string;
  industries: string[];
}

interface ExpertConsultationProps {
  title: string;
  description: string;
  experts: Expert[];
}

const ExpertConsultation = ({ title, description, experts }: ExpertConsultationProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedExpert, setSelectedExpert] = useState<string | null>(null);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleExpertSelect = (expertId: string) => {
    setSelectedExpert(expertId);
  };

  if (!isHydrated) {
    return (
      <section className="py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground mb-4">
              {title}
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              {description}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-card rounded-lg p-6 shadow-sm animate-pulse">
                <div className="w-24 h-24 bg-muted rounded-full mx-auto mb-4" />
                <div className="h-4 bg-muted rounded mb-2" />
                <div className="h-3 bg-muted rounded" />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-16 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="flex items-center justify-center space-x-3 mb-4">
            <Icon name="UserGroupIcon" size={32} variant="solid" className="text-primary" />
            <h2 className="text-3xl md:text-4xl font-headline font-bold text-foreground">
              {title}
            </h2>
          </div>
          <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
            {description}
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {experts.map((expert) => (
            <div
              key={expert.id}
              className={`bg-card rounded-lg p-6 shadow-sm hover:shadow-md transition-all duration-base cursor-pointer ${
                selectedExpert === expert.id ? 'ring-2 ring-accent' : ''
              }`}
              onClick={() => handleExpertSelect(expert.id)}
            >
              <div className="relative w-24 h-24 mx-auto mb-4 overflow-hidden rounded-full">
                <AppImage
                  src={expert.image}
                  alt={expert.alt}
                  className="w-full h-full object-cover"
                />
              </div>
              
              <div className="text-center">
                <h3 className="text-lg font-headline font-semibold text-foreground mb-1">
                  {expert.name}
                </h3>
                <p className="text-sm text-accent font-body font-semibold mb-2">
                  {expert.title}
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  {expert.specialization}
                </p>
                
                <div className="flex items-center justify-center space-x-2 text-xs text-muted-foreground mb-4">
                  <Icon name="BriefcaseIcon" size={14} variant="outline" />
                  <span>{expert.experience}</span>
                </div>
                
                <div className="flex flex-wrap gap-2 justify-center">
                  {expert.industries.slice(0, 2).map((industry, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-muted text-xs text-muted-foreground rounded-full"
                    >
                      {industry}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {selectedExpert && (
          <div className="bg-gradient-to-r from-primary to-secondary rounded-lg p-8 text-center">
            <h3 className="text-2xl font-headline font-bold text-primary-foreground mb-4">
              Ready to discuss your {experts.find(e => e.id === selectedExpert)?.specialization} needs?
            </h3>
            <p className="text-primary-foreground opacity-90 mb-6 max-w-2xl mx-auto">
              Schedule a consultation with {experts.find(e => e.id === selectedExpert)?.name} to explore how Invictosoft can transform your industry challenges into competitive advantages.
            </p>
            <button className="px-8 py-3 bg-conversion text-conversion-foreground font-headline font-semibold rounded-md hover:bg-opacity-90 shadow-cta hover:shadow-partnership transition-all duration-base transform hover:-translate-y-0.5">
              Schedule Consultation
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default ExpertConsultation;